/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

/**
 * Number of milliseconds in a day: 24 * 60 * 60 * 1000
 * 1 day = 24hrs
 * 1 hour = 60 mins
 * 1 min = 60 secs
 * 1 sec = 1000 ms
 */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Midnight (local) of the given date, as ms. */
const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Returns the time part of the given date, as a string.
 */
const timePart = (d: Date): string =>
  d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * Formats a conversation timestamp relative to now:
 *   Today          -> "2:45 PM"
 *   Yesterday      -> "Yesterday" (or "Yesterday, 2:45 PM" when withTime)
 *   Last 7 days    -> "Monday"
 *   This year      -> "Sep 12"
 *   Earlier years  -> "Sep 12, 2025"
 * The "withTime" option controls whether the time should be shown for yesterday.
 */
export const formatConversationTimestamp = (
  ts: number,
  options?: { withTime?: boolean }
): string => {
  if (!ts) return '';

  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / MS_PER_DAY);

  // Future timestamps (clock skew) fall back to today's behaviour.
  if (dayDiff <= 0) return timePart(date);

  if (dayDiff === 1) {
    return options?.withTime ? `Yesterday, ${timePart(date)}` : 'Yesterday';
  }

  if (dayDiff < 7) return date.toLocaleDateString([], { weekday: 'long' });

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString([], { month: 'short', day: '2-digit' });
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};
